// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Remembering where the machine stands, between sessions (M5-17).
//
// Two halves that are easy to confuse and must not be:
//
// - **The anchor** is the runtime's business. It is a handle to a point the headset's own map
//   knows about, it survives the room being re-scanned, and it is the only thing that can put the
//   machine back on the same real table tomorrow. It cannot be serialised and there is nothing
//   useful to do with it except hand it back to the runtime.
// - **The persisted record** is ours. It is the anchor's *handle string* — what
//   `XRAnchor.requestPersistentHandle()` returns — plus the scale and yaw the player chose, which
//   the anchor does not carry.
//
// So restoring a placement is: read the record, ask the runtime to restore the anchor from the
// handle, ask the frame where that anchor is now, and rebuild the placement from the anchor's
// position plus our own yaw and scale. **Any step can fail and every failure means the same
// thing** — go back to safe placement (06 §3), never guess. A machine placed at yesterday's
// coordinates in a room the headset no longer recognises is a machine standing through a wall.
//
// **Anchors are optional** (`features.ts`), so all of this is best-effort by construction. On a
// runtime without them the scale and yaw are still remembered and only the position is asked for
// again, which is a much smaller ritual than the first one.

import { readLocal, removeLocal, writeLocal, type StorageLike } from '../storage/local'
import { clampScale, type Placement, type PlacementScale } from './placement'

/** Where the record lives. Namespaced like everything else this product writes. */
export const ANCHOR_KEY = 'hcc.pinball.xr.anchor'

/** What is kept between sessions. */
export interface AnchorRecord {
  /** The runtime's persistent handle, or undefined on a runtime without anchors. */
  readonly handle?: string
  readonly yaw: number
  readonly scale: number
  readonly mode: PlacementScale
}

/** The slice of an `XRAnchor` this product uses. */
export interface XRAnchorPort {
  readonly anchorSpace: unknown
  requestPersistentHandle?: () => Promise<string>
  delete?: () => void
}

/** The slice of an `XRFrame` that creates anchors. */
export interface XRAnchorFramePort {
  createAnchor?: (pose: unknown, space: unknown) => Promise<XRAnchorPort>
}

/** The slice of an `XRSession` that deals in anchors. */
export interface XRAnchorSessionPort {
  restorePersistentAnchor?: (handle: string) => Promise<XRAnchorPort>
  deletePersistentAnchor?: (handle: string) => Promise<void>
}

/** Read the record, or undefined when there is none or it is unreadable. */
export function readAnchorRecord(storage: StorageLike | undefined): AnchorRecord | undefined {
  const raw = readLocal(storage, ANCHOR_KEY)
  if (raw === undefined) return undefined
  try {
    const parsed = JSON.parse(raw) as Partial<AnchorRecord>
    // **`typeof x === 'number'` is true of `Infinity`, and this record has a field that reaches a
    // rotation.** `1e400` is valid JSON, parses to `Infinity`, and passed straight through here
    // into `yaw` — where `Math.cos` and `Math.sin` answer `NaN` and the cabinet's quaternion goes
    // with them. The asymmetry gave it away rather than any failure: `scale` two lines down is
    // handed to `clampScale`, which refuses a non-finite value explicitly, so the same untrusted
    // record was treated as untrusted in one field and trusted in the other.
    //
    // **Corrupt rather than clamped, and the difference is that yaw has no sensible default.**
    // `clampScale` can fall back to a tabletop size nobody objects to; a facing cannot — M5-33
    // records that a machine left at yaw 0 in front of a player facing north-east "has its side
    // panel to them, which reads as a placement that half worked". So this takes the policy this
    // function already states: *a corrupt record is the same as no record*, and the ritual runs.
    if (
      typeof parsed.yaw !== 'number' ||
      typeof parsed.scale !== 'number' ||
      !Number.isFinite(parsed.yaw)
    ) {
      return undefined
    }
    const mode: PlacementScale = parsed.mode === 'tabletop' ? 'tabletop' : 'full'
    return {
      ...(typeof parsed.handle === 'string' ? { handle: parsed.handle } : {}),
      yaw: parsed.yaw,
      scale: clampScale(mode, parsed.scale),
      mode,
    }
  } catch {
    // A corrupt record is the same as no record: the ritual runs and writes a good one.
    return undefined
  }
}

/** Write the record. Best effort — a browser with storage disabled simply re-places each time. */
export function writeAnchorRecord(storage: StorageLike | undefined, record: AnchorRecord): void {
  writeLocal(storage, ANCHOR_KEY, JSON.stringify(record))
}

export function forgetAnchor(storage: StorageLike | undefined): void {
  removeLocal(storage, ANCHOR_KEY)
}

/**
 * Give the headset back an anchor this product is finished with (M9-54).
 *
 * **`deletePersistentAnchor` was declared in this file and called by nothing.** `forgetAnchor`
 * above removes *our* record and `XRAnchorPort.delete` releases the *in-session* object; neither
 * touches the entry in the runtime's own persistent store, which is the thing a handle is a key
 * into. So every placement this product has ever remembered is still in the headset's store — and
 * the explicit forgets are not even where most of them came from: `rememberPlacement` mints a
 * fresh handle on every placement and overwrites the record, and the handle it overwrote became
 * unreachable rather than released. A player who has moved their machine a hundred times has left
 * a hundred behind.
 *
 * **Best effort, and never awaited by a caller with something else to do.** The runtime may not
 * implement it — most do not today — the handle may already be gone, and the session may be
 * ending. None of those is worth failing a placement over: what is being cleaned up is somebody
 * else's bookkeeping.
 */
export async function releaseAnchor(
  session: XRAnchorSessionPort | undefined,
  handle: string | undefined,
): Promise<void> {
  if (session?.deletePersistentAnchor === undefined || handle === undefined) return
  try {
    await session.deletePersistentAnchor(handle)
  } catch {
    // A handle the runtime has already forgotten, or a session that has ended. Both are the
    // outcome this was asking for.
  }
}

/**
 * The handle the stored record is holding, if it has one.
 *
 * Exists so a caller can release the old handle *before* something overwrites it, which is the
 * only moment it is still reachable.
 */
export function rememberedHandle(storage: StorageLike | undefined): string | undefined {
  return readAnchorRecord(storage)?.handle
}

/** What happened when a remembered placement was asked for. */
export type RestoreOutcome =
  /** The anchor came back. Its *position* still has to be located in a frame — see below. */
  | { readonly kind: 'anchor'; readonly anchor: XRAnchorPort; readonly record: AnchorRecord }
  /** There was a record but the anchor could not be restored. The player must re-place. */
  | { readonly kind: 'lost'; readonly mode: PlacementScale; readonly scale: number }
  /** Nothing was remembered. First run, or the player asked to forget. */
  | { readonly kind: 'none' }

/**
 * Ask the runtime for the anchor this product remembered.
 *
 * **Deliberately stops at the anchor and does not produce a `Placement`.** Restoring is
 * asynchronous and locating is per-frame: `restorePersistentAnchor` returns a promise, and an
 * anchor's *position* can only be read inside an `XRFrame` against that frame's reference space.
 * A single call that tried to do both would either have to block a frame callback on a promise or
 * hold on to a stale frame, and both are the kind of thing that works in a test and fails on a
 * headset. So this half is async and `placementFromAnchor` is the half a frame calls.
 *
 * Every failure path lands on `lost` rather than throwing, and `lost` carries the scale and the
 * mode so re-placing is one pinch rather than a whole setup: the player's *choices* survive even
 * when the room's geometry does not.
 */
export async function restoreAnchor(
  storage: StorageLike | undefined,
  session: XRAnchorSessionPort,
): Promise<RestoreOutcome> {
  const record = readAnchorRecord(storage)
  if (record === undefined) return { kind: 'none' }
  const lost = { kind: 'lost', mode: record.mode, scale: record.scale } as const
  if (record.handle === undefined || session.restorePersistentAnchor === undefined) return lost
  try {
    const anchor = await session.restorePersistentAnchor(record.handle)
    return { kind: 'anchor', anchor, record }
  } catch {
    return lost
  }
}

/**
 * How far below the eyeline a machine's bar has to sit to be one somebody could play, metres.
 *
 * Small. This is not a comfort rule — `SEATED_BAR_BELOW_EYELINE` is, and it asks for 0.30 m. This
 * only has to separate "a machine somebody placed" from "a machine standing on furniture it does
 * not fit on", and it must not second-guess a player who genuinely likes their cabinet high.
 *
 * The same 0.05 as `AnchorStore.RestoredBarBelowEye` on the Unity side. The two stacks have to
 * agree about what a playable cabinet is, or the same room gives two different answers.
 */
export const RESTORED_BAR_BELOW_EYE = 0.05

/**
 * Whether a machine standing here is one this player could play.
 *
 * **The bar against the eyeline, and nothing else.** A cabinet you have to look *up* at is one you
 * cannot play — the playfield is a horizontal surface, and above your eyeline you see its underside.
 * Distance is deliberately not part of this: a player may walk away from their machine and come
 * back to it, and `clampToReach` is what has opinions about how far away things are.
 *
 * Found on the headset (25 Aug 2026): a seated player with a 1.22 m eyeline had a full-size cabinet
 * placed on a 0.76 m desk, putting its lockdown bar at 1.674 m — 0.45 m above their own eyes, in a
 * placement that was visible, upright, correctly scaled and completely unplayable. Nothing in
 * either client's placement path had an opinion about height; both had one about distance.
 */
export function barIsPlayable(barY: number, eyeHeight: number): boolean {
  return barY <= eyeHeight - RESTORED_BAR_BELOW_EYE
}

/**
 * How far below the floor a machine's base may sit and still be standing on it, metres.
 *
 * The same quantity `FLOOR_TOLERANCE` names from the other direction — how close to the floor
 * counts as the floor — and written here rather than there so the two files depend on each other
 * in one direction only. The same 0.05 as `AnchorStore.SunkenTolerance` on the Unity side.
 */
export const SUNKEN_TOLERANCE = 0.05

/**
 * Whether a machine standing here is standing on something this room has.
 *
 * **The other half of `barIsPlayable`, and it was missing from both stacks.** Found on the headset
 * (25 Aug 2026): an anchor restored at y = **-0.41** against a believable 1.27 m eyeline, forty-one
 * centimetres underground. The bar rule accepted it and was right by its own terms — the lockdown
 * bar landed at 0.50 m, three-quarters of a metre below the eyeline. It only ever looked up.
 *
 * A saved placement is only as good as the floor it was saved against, and a floor re-measured
 * higher buries yesterday's cabinet exactly as a floor re-measured lower hangs it from the ceiling.
 * **Refused rather than lifted**, because an anchor whose height came from the wrong floor took its
 * x and z from there too.
 *
 * The tolerance is not slack: a seated player at a 1.27 m eyeline who likes the bar 0.40 m below it
 * puts a full-size cabinet's base at -0.044, and that is a placement this product makes on purpose.
 */
export function standsInTheRoom(y: number): boolean {
  return y >= -SUNKEN_TOLERANCE
}

/** Which way a restored placement is wrong, when it is. */
export type RestoreDoubt = 'ceiling' | 'ground' | 'nowhere'

/**
 * Whether yesterday's placement is one this room can still hold, and if not, which way it is wrong.
 *
 * **Both halves in one call, because the browser only ever asked one of them.** `standsInTheRoom`
 * was wired into `XRRuntime`'s restore on 25 Aug; `barIsPlayable` never was. Its doc comment above
 * is written entirely about a restored anchor — a full-size cabinet on a 0.76 m desk with its bar
 * 0.45 m above a seated player's eyes — and its only caller is the *live* hit test, which judges a
 * placement the player is making now. The one it was written for went unjudged.
 *
 * So the browser refused a cabinet in the ground and accepted one in the ceiling. That is exactly
 * the placement the Quest client's `MixedRealityRig.Judge` was written for on 22 Aug: an anchor
 * recorded while the runtime reported a 3.98 m eye height, restored perfectly into a corrected
 * space, 2.7 m away with its lockdown bar 0.14 m **above** the player's eyeline. `StillBelievable`
 * landed on the headset that day and this half of the pair was left out.
 *
 * Same two rules in the same order as `Judge`, so the two stacks refuse the same placements. A
 * machine the headset sends back to the reticle and the browser stands in the ceiling is one room
 * giving two different answers, which is the thing `RESTORED_BAR_BELOW_EYE` says out loud it
 * exists to prevent.
 */
export function doubtsAboutRestore(
  y: number,
  barY: number,
  eyeHeight: number,
): RestoreDoubt | undefined {
  // **A number that is not a measurement is refused first, rather than left to the two rules.**
  // Both of them would in fact catch a non-finite value here — `barIsPlayable` refuses `NaN` and
  // `+∞` because every comparison against `NaN` is false and an infinite bar is above any eyeline —
  // but `standsInTheRoom` is a one-sided range check and `Infinity >= -0.05` is **true**, so the
  // pair is only safe while the caller derives `barY` from `y`. That coupling is exactly the kind
  // this directory has been bitten by: `readAnchorRecord` guards the stored yaw, and the anchor's
  // *position* never passes through it — it comes straight from the runtime.
  if (!Number.isFinite(y) || !Number.isFinite(barY) || !Number.isFinite(eyeHeight)) return 'nowhere'
  if (!barIsPlayable(barY, eyeHeight)) return 'ceiling'
  if (!standsInTheRoom(y)) return 'ground'
  return undefined
}

/**
 * The placement a located anchor implies: its position, **its own yaw** when the runtime gives one,
 * and the scale we remembered.
 *
 * **The remembered yaw is in the space the anchor was made in, and that space is gone** (14 Sep
 * 2026). The runtime tracks an anchor as a *pose* — world-locked, so its orientation in a later
 * session's space is the machine's true facing in that space. The record's yaw is the same angle
 * written down in the old space's terms, and `local-floor`'s "forward" is wherever the head
 * pointed when that session began or last recentred. Restored from the record, a machine that
 * faced the player came back turned by the difference between the two forwards — "orientating to
 * the wall, not the user's facing position". The record's yaw is kept for a runtime that hands
 * back a position and no orientation, and for a restore with no anchor at all.
 */
export function placementFromAnchor(
  record: AnchorRecord,
  at: { readonly x: number; readonly y: number; readonly z: number },
  orientation?: Orientation | undefined,
): Placement {
  const yaw = orientation === undefined ? record.yaw : yawOfOrientation(orientation)
  return { x: at.x, y: at.y, z: at.z, yaw, scale: record.scale }
}

/** A quaternion as the runtime reports one. */
export interface Orientation {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly w: number
}

/**
 * The anchor's orientation for a placement: the rotation the drawn machine is given.
 *
 * `Placement.yaw` turns the other way from a rotation about up — the front at yaw θ is
 * `(−sin θ, cos θ)`, which is why `XRRuntime.applyPlacement` writes `rotation.y = −yaw`. Until
 * 14 Sep 2026 the anchor was made with `+yaw`, the mirror: harmless while the anchor was only a
 * position, and exactly wrong once its orientation was read — a space turned by Δ turns the
 * mirror by Δ too, and reading it back through the mirror gives an error of 2Δ.
 */
export function orientationOfYaw(yaw: number): Orientation {
  return { x: 0, y: -Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) }
}

/**
 * The yaw of a pose: where its +Z — the cabinet's front, `Placement.yaw`'s convention — points on
 * the ground. Read from the rotated axis rather than from `2·atan2(y, w)`, which is only right for
 * a rotation purely about up, and a tracked anchor is never quite that. The inverse of
 * `orientationOfYaw`.
 */
export function yawOfOrientation(q: Orientation): number {
  // (0, 0, 1) rotated by q, x and z components.
  const fx = 2 * (q.x * q.z + q.w * q.y)
  const fz = 1 - 2 * (q.x * q.x + q.y * q.y)
  if (fx === 0 && fz === 0) return 0
  return Math.atan2(-fx, fz)
}

/**
 * Create an anchor where the machine now stands, if the runtime does anchors.
 *
 * `undefined` on a runtime that does not, which is not a failure: the scale and the yaw are still
 * remembered and only the position is asked for again next time.
 */
export async function createAnchorAt(
  frame: XRAnchorFramePort,
  referenceSpace: unknown,
  placement: Placement,
): Promise<XRAnchorPort | undefined> {
  if (frame.createAnchor === undefined) return undefined
  try {
    return await frame.createAnchor(
      {
        position: { x: placement.x, y: placement.y, z: placement.z, w: 1 },
        // Yaw only, as a quaternion about the room's up axis — a cabinet stands on a level floor
        // and `Placement` has no pitch or roll to encode. `orientationOfYaw`, so the anchor turns
        // the way the drawn machine turns and its pose reads back as the yaw it was made with.
        orientation: orientationOfYaw(placement.yaw),
      },
      referenceSpace,
    )
  } catch {
    return undefined
  }
}

/**
 * Remember a placement, anchoring it where the runtime can.
 *
 * The record is written **whether or not** the anchor persists, because the yaw and the scale are
 * worth remembering on their own: a player who chose tabletop at 0.38 and turned the machine 20°
 * to the left should get that back tomorrow even on a runtime that cannot hold the position.
 */
export async function rememberPlacement(
  storage: StorageLike | undefined,
  placement: Placement,
  mode: PlacementScale,
  anchor: XRAnchorPort | undefined,
  /**
   * The session, so the handle this call is about to orphan can be given back (M9-54).
   *
   * **This is where the leak accumulated**, rather than at either of the two places a player
   * explicitly forgets a placement: re-placing the machine is the common act, it mints a new
   * handle, and the record it overwrites was the only thing that knew the old one.
   *
   * Optional because a runtime without anchors has no session to give and a test may not care —
   * and `test/xr/anchors.test.ts` refuses a caller in `src/` that leaves it out, because *an
   * optional argument nobody passes* is this repository's most-recorded way of shipping a fix
   * that does nothing.
   */
  session?: XRAnchorSessionPort | undefined,
): Promise<AnchorRecord> {
  // Read before the write, because the record is about to stop naming it.
  const previous = rememberedHandle(storage)
  let handle: string | undefined
  if (anchor?.requestPersistentHandle !== undefined) {
    try {
      handle = await anchor.requestPersistentHandle()
    } catch {
      handle = undefined
    }
  }
  const record: AnchorRecord = {
    ...(handle === undefined ? {} : { handle }),
    yaw: placement.yaw,
    scale: placement.scale,
    mode,
  }
  writeAnchorRecord(storage, record)
  // **After the write, and not awaited.** The record naming the new handle is what a crash between
  // these two lines must leave behind: an orphan in the runtime's store costs a slot, and a record
  // naming a handle that was deleted costs the player their placement.
  if (previous !== undefined && previous !== handle) void releaseAnchor(session, previous)
  return record
}
